import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import Button from '../../components/ui/Button';
import SearchAndFilters from './components/SearchAndFilters';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import UserTable from './components/UserTable';
import UserMobileCard from './components/UserMobileCard';
import PaginationControls from './components/PaginationControls';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import StatusToggleModal from './components/StatusToggleModal';

const UserManagement = () => {
  const navigate = useNavigate();
  
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isMobile, setIsMobile] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null, isMultiple: false });
  const [statusModal, setStatusModal] = useState({ isOpen: false, user: null, newStatus: '' });
  
  // Filter state
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    lastLoginRange: ''
  });

  // Mock user data with role-based access
  const mockUsers = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@party.com",
      role: "admin",
      status: "active",
      lastLogin: "2024-01-15T10:30:00Z",
      createdAt: "2024-01-01T08:00:00Z"
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      email: "michael.rodriguez@party.com", 
      role: "manager",
      status: "active",
      lastLogin: "2024-01-14T16:45:00Z",
      createdAt: "2024-01-05T09:15:00Z"
    },
    {
      id: 3,
      name: "Emily Chen",
      email: "emily.chen@party.com",
      role: "viewer",
      status: "active", 
      lastLogin: "2024-01-10T12:20:00Z",
      createdAt: "2024-01-03T14:30:00Z"
    },
    {
      id: 4,
      name: "David Thompson", 
      email: "david.thompson@party.com",
      role: "manager",
      status: "inactive",
      lastLogin: "2024-01-08T09:15:00Z",
      createdAt: "2024-01-02T11:45:00Z"
    },
    {
      id: 5,
      name: "Lisa Anderson",
      email: "lisa.anderson@party.com",
      role: "viewer",
      status: "active",
      lastLogin: "2024-01-12T14:30:00Z", 
      createdAt: "2024-01-04T16:20:00Z"
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson@party.com",
      role: "admin",
      status: "active",
      lastLogin: "2024-01-13T11:00:00Z",
      createdAt: "2024-01-06T13:10:00Z"
    },
    {
      id: 7,
      name: "Maria Garcia", 
      email: "maria.garcia@party.com",
      role: "manager",
      status: "active",
      lastLogin: "2024-01-11T15:45:00Z",
      createdAt: "2024-01-07T10:30:00Z"
    },
    {
      id: 8,
      name: "Robert Brown",
      email: "robert.brown@party.com", 
      role: "viewer",
      status: "active",
      lastLogin: "2024-01-09T13:20:00Z",
      createdAt: "2024-01-08T12:15:00Z"
    }
  ];

  // Initialize data
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setUsers(mockUsers);
      setLoading(false);
    };

    loadUsers();
  }, []);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter and search logic
  const filteredUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      // Search filter
      const searchLower = searchTerm?.toLowerCase();
      const matchesSearch = !searchTerm || 
        user?.name?.toLowerCase()?.includes(searchLower) ||
        user?.email?.toLowerCase()?.includes(searchLower);

      // Role filter
      const matchesRole = !filters?.role || user?.role === filters?.role;

      // Status filter
      const matchesStatus = !filters?.status || user?.status === filters?.status;

      // Last login range filter
      let matchesLastLogin = true;
      if (filters?.lastLoginRange && user?.lastLogin) {
        const loginDate = new Date(user.lastLogin);
        const now = new Date();
        
        switch (filters?.lastLoginRange) {
          case 'today':
            matchesLastLogin = loginDate?.toDateString() === now?.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesLastLogin = loginDate >= weekAgo;
            break;
          case 'month':
            matchesLastLogin = loginDate?.getMonth() === now?.getMonth() && 
                             loginDate?.getFullYear() === now?.getFullYear();
            break;
          case 'never':
            matchesLastLogin = !user?.lastLogin;
            break;
        }
      }

      return matchesSearch && matchesRole && matchesStatus && matchesLastLogin;
    });

    // Sort filtered results
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'lastLogin' || sortConfig?.key === 'createdAt') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [users, searchTerm, filters, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers?.length / itemsPerPage);
  const paginatedUsers = filteredUsers?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Event handlers
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      role: '',
      status: '',
      lastLoginRange: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSort = (config) => {
    setSortConfig(config);
  };

  const handleSelectUser = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev?.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(paginatedUsers?.map(user => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleDeselectAll = () => {
    setSelectedUsers([]);
  };

  const handleEditUser = (user) => {
    navigate('/user-creation', { state: { editUser: user } });
  };

  const handleDeleteUser = (user) => {
    setDeleteModal({
      isOpen: true,
      user,
      isMultiple: false
    });
  };

  const handleToggleUserStatus = (user) => {
    const newStatus = user?.status === 'active' ? 'inactive' : 'active';
    setStatusModal({
      isOpen: true,
      user,
      newStatus
    });
  };

  const handleBulkDelete = () => {
    setDeleteModal({
      isOpen: true,
      user: null,
      isMultiple: true
    });
  };

  const handleBulkRoleUpdate = (role) => {
    // Simulate bulk role update
    setUsers(prev => prev?.map(user => 
      selectedUsers?.includes(user?.id) 
        ? { ...user, role }
        : user
    ));
    setSelectedUsers([]);
  };

  const handleBulkStatusUpdate = (status) => {
    // Simulate bulk status update
    setUsers(prev => prev?.map(user => 
      selectedUsers?.includes(user?.id) 
        ? { ...user, status }
        : user
    ));
    setSelectedUsers([]);
  };

  const confirmDelete = () => {
    if (deleteModal?.isMultiple) {
      setUsers(prev => prev?.filter(user => !selectedUsers?.includes(user?.id)));
      setSelectedUsers([]);
    } else {
      setUsers(prev => prev?.filter(user => user?.id !== deleteModal?.user?.id));
    }
    setDeleteModal({ isOpen: false, user: null, isMultiple: false });
  };

  const confirmStatusToggle = () => {
    setUsers(prev => prev?.map(user => 
      user?.id === statusModal?.user?.id 
        ? { ...user, status: statusModal?.newStatus }
        : user
    ));
    setStatusModal({ isOpen: false, user: null, newStatus: '' });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Role counts for filtering
  const roleCounts = useMemo(() => {
    return users?.reduce((acc, user) => {
      acc[user?.role] = (acc?.[user?.role] || 0) + 1;
      return acc;
    }, {});
  }, [users]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Cabeçalho da página */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Gestão de Usuários</h1>
              <p className="text-muted-foreground">
                Gerencie os usuários do sistema com controle de acesso por perfis
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/user-creation')}
                iconName="UserPlus"
                iconPosition="left"
              >
                Adicionar usuário
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <SearchAndFilters
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            totalResults={filteredUsers?.length}
            roleCounts={roleCounts}
          />

          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedUsers?.length}
            onBulkDelete={handleBulkDelete}
            onBulkRoleUpdate={handleBulkRoleUpdate}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onDeselectAll={handleDeselectAll}
          />

          {/* User List */}
          {isMobile ? (
            <div className="space-y-4">
              {paginatedUsers?.map((user) => (
                <UserMobileCard
                  key={user?.id}
                  user={user}
                  isSelected={selectedUsers?.includes(user?.id)}
                  onSelect={handleSelectUser}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  onToggleStatus={handleToggleUserStatus}
                />
              ))}
            </div>
          ) : (
            <UserTable
              users={paginatedUsers}
              sortConfig={sortConfig}
              onSort={handleSort}
              selectedUsers={selectedUsers}
              onSelectUser={handleSelectUser}
              onSelectAll={handleSelectAll}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUser}
              onToggleStatus={handleToggleUserStatus}
            />
          )}

          {/* Pagination */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredUsers?.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal?.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null, isMultiple: false })}
        onConfirm={confirmDelete}
        userName={deleteModal?.user?.name}
        isMultiple={deleteModal?.isMultiple}
        count={selectedUsers?.length}
      />

      {/* Status Toggle Modal */}
      <StatusToggleModal
        isOpen={statusModal?.isOpen}
        onClose={() => setStatusModal({ isOpen: false, user: null, newStatus: '' })}
        onConfirm={confirmStatusToggle}
        userName={statusModal?.user?.name}
        currentStatus={statusModal?.user?.status}
        newStatus={statusModal?.newStatus}
      />

      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loading}
        message="Carregando dados de usuários..."
      />
    </div>
  );
};

export default UserManagement;